<template>
  <div class='pb-5 mt-2'>
    <section class='d-flex justify-content-between align-items-center mb-3'>
      <h4 class='page-title'>Gestão de novos MVPs</h4>
      <b-button variant='primary' @click="$refs.addMvp.open(null)">
        <b-icon icon='plus' /> Novo MVP
      </b-button>
    </section>

    <p class='intro'>
      Cadastre funcionalidades em teste (MVP). Os MVPs ativos aparecem como um aviso no topo de todas as abas;
      ao clicar, o usuário abre o conteúdo em uma nova aba. Use o botão de métricas para ver quem usou,
      quanto usou e se achou útil.
    </p>

    <base-table
      v-if='mvps && mvpsMeta'
      :columns='columns'
      :data='mvps'
      :total-items='mvpsMeta.total'
      :start-items='(mvpsMeta.skip || 0) + 1'
      :max-items='mvpsMeta.limit || 10'
      @set-page='handleChangePage'
    >
      <template v-slot:body-active='row'>
        {{ row.item.active ? 'Sim' : 'Não' }}
      </template>

      <template v-slot:body-source='row'>
        <a :href='sourceUrl(row.item)' target='_blank'>visualizar</a>
      </template>

      <template v-slot:body-actions='row'>
        <b-button
          variant='outline-primary'
          size='sm'
          class='mr-1'
          title='Métricas de uso'
          @click="$router.push({ name: 'MvpMetrics', params: { id: row.item._id } })"
        >
          <b-icon icon='bar-chart-fill' />
        </b-button>
        <b-button
          variant='outline-info'
          size='sm'
          class='mr-1'
          title='Copiar link do MVP'
          @click='copyUrl(row.item)'
        >
          <b-icon icon='clipboard' />
        </b-button>
        <b-button
          variant='outline-success'
          size='sm'
          class='mr-1'
          @click="$refs.addMvp.open(row.item)"
        >
          <b-icon icon='pencil-fill' />
        </b-button>
        <b-button
          variant='outline-danger'
          size='sm'
          @click='confirmRemove(row.item)'
        >
          <b-icon icon='trash-fill' />
        </b-button>
      </template>
    </base-table>

    <add-mvp ref='addMvp' @saved='onSaved' />

    <modal-confirm-action
      ref-modal='modalConfirmRemoveMvp'
      id-modal='modalConfirmRemoveMvp'
      variant='danger'
      title='Remover MVP'
      canceltext='Cancelar'
      ok-text='Remover'
      :description='removeDescription'
      :loading='removing'
      @confirmed='remove'
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddMvp from '@/components/Mvp/AddMvp'
import ModalConfirmAction from '@/components/ModalConfirmAction'

export default {
  name: 'ManageMvps',
  components: { AddMvp, ModalConfirmAction },
  data() {
    return {
      mvpToRemove: null,
      removing: false,
      columns: [
        { key: 'title', value: 'Título' },
        { key: 'source', value: 'Conteúdo' },
        { key: 'active', value: 'Ativo' },
        { key: 'actions', value: 'Ações' }
      ]
    }
  },
  computed: {
    ...mapGetters('mvp', {
      mvps: 'getMvps',
      mvpsMeta: 'getMvpsMeta'
    }),
    removeDescription() {
      const title = this.mvpToRemove ? this.mvpToRemove.title : ''
      return `Deseja realmente remover o MVP <strong>${title}</strong>?`
    }
  },
  methods: {
    ...mapActions('mvp', {
      handleGetMvps: 'handleGetMvps',
      handleDeleteMvp: 'handleDeleteMvp'
    }),
    ...mapActions('breadcrumb', {
      setBreadcrumb: 'setBreadcrumb'
    }),
    sourceUrl(mvp) {
      return this.$router.resolve({
        name: 'MvpView',
        params: { slug: mvp.slug },
        query: { preview: mvp._id }
      }).href
    },
    async copyUrl(mvp) {
      const path = this.$router.resolve({ name: 'MvpView', params: { slug: mvp.slug } }).href
      const url = `${window.location.origin}${path}`
      try {
        await navigator.clipboard.writeText(url)
      } catch (e) {
        const el = document.createElement('textarea')
        el.value = url
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.$bvToast.toast(
        mvp.active ? url : `${url} — o MVP está inativo; o link só funcionará quando ativado.`,
        {
          title: mvp.active ? 'Link copiado!' : 'Link copiado — MVP inativo',
          variant: mvp.active ? 'success' : 'warning',
          toaster: 'b-toaster-top-center',
          autoHideDelay: mvp.active ? 4000 : 6000
        }
      )
    },
    handleChangePage(page) {
      const pageCount = page <= 0 ? 1 : page
      this.loadMvps({
        skip: (pageCount - 1) * ((this.mvpsMeta && this.mvpsMeta.limit) || 10)
      })
    },
    async loadMvps(payload) {
      await this.handleGetMvps({
        skip: (payload && payload.skip) || 0,
        limit: (this.mvpsMeta && this.mvpsMeta.limit) || 10
      })
    },
    onSaved() {
      this.loadMvps()
      this.$bvToast.toast('MVP salvo com sucesso!', {
        title: 'Atenção',
        variant: 'success',
        toaster: 'b-toaster-top-center',
        autoHideDelay: 4000
      })
    },
    confirmRemove(mvp) {
      this.mvpToRemove = mvp
      this.$bvModal.show('modalConfirmRemoveMvp')
    },
    async remove() {
      if (!this.mvpToRemove) {
        return
      }
      this.removing = true
      try {
        await this.handleDeleteMvp(this.mvpToRemove._id)
        this.$bvModal.hide('modalConfirmRemoveMvp')
        this.mvpToRemove = null
        await this.loadMvps()
        this.$bvToast.toast('MVP removido!', {
          title: 'Atenção',
          variant: 'success',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 4000
        })
      } catch (e) {
        this.$bvToast.toast('Não foi possível remover o MVP. Tente novamente.', {
          title: 'Atenção',
          variant: 'danger',
          toaster: 'b-toaster-top-center',
          autoHideDelay: 5000
        })
      } finally {
        this.removing = false
      }
    }
  },
  created() {
    this.setBreadcrumb({
      tab: '',
      page: 'Gestão de novos MVPs',
      subPage: {
        name: 'Gestão de novos MVPs',
        active: false
      },
      tabs: [
        {
          name: 'MVPs',
          active: true
        }
      ]
    })
  },
  async mounted() {
    await this.loadMvps()
  }
}
</script>

<style lang='scss' scoped>
.page-title {
  color: #04a7f3;
  font-weight: 700;
  margin: 0;
}

.intro {
  color: #808080;
  font-size: 14px;
  margin-bottom: 16px;
}
</style>
